import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  linkedSignal,
  output,
  Signal,
  WritableSignal
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { CarouselDialogData } from '@app/components/dialogs/carousel-dialog/carousel-dialog.component';
import { HorizontalFileItemComponent } from '@app/shared/components/files/file-horizontal-list/horizontal-file-item/horizontal-file-item.component';
import { FileActionsService } from '@app/shared/services/actions/file-actions.service';
import { FileDataService } from '@app/shared/services/file-data.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { FilePreviewDialogService } from '@app/shared/services/shared/file-preview-dialog.service';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { FileUploadInfo } from '@app/shared/types/models/files/file-upload-info';
import { ALLOWED_FILES_TYPES } from '@app/shared/utils/allowed-files-types';
import { TranslatePipe } from '@ngx-translate/core';
import { FileUploadListComponent } from '../file-upload-list/file-upload-list.component';
import { isFileViewable } from '../file.utils';

@Component({
  selector: 'proffeo-file-horizontal-list',
  templateUrl: './file-horizontal-list.component.html',
  styleUrl: './file-horizontal-list.component.scss',
  imports: [TranslatePipe, MatExpansionModule, MatIconModule, HorizontalFileItemComponent, FileUploadListComponent]
})
export class FileHorizontalListComponent {
  private readonly filePreviewDialogService = inject(FilePreviewDialogService);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly fileService = inject(FileDataService);
  private readonly fileActions = inject(FileActionsService);

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected localFiles: WritableSignal<FileInfo[]> = linkedSignal(() => this.files());
  protected viewableFiles: Signal<FileInfo[]> = computed(() => this.localFiles().filter(f => isFileViewable(f)));

  public readonly delete = output<FileInfo>();
  public readonly avatarSet = output<FileInfo>();
  public allowedFilesTypes: InputSignal<string[]> = input(ALLOWED_FILES_TYPES);
  public files: InputSignal<FileInfo[]> = input([]);
  public uploadingFiles: InputSignal<FileUploadInfo[]> = input([]);
  public canSetAsAvatar: InputSignal<boolean> = input(false);
  public avatarFileId: InputSignal<string | null> = input(null);

  protected onDeleteFile(fileId: string): void {
    const file = this.localFiles().find(f => f.id === fileId);
    if (!file) {
      return;
    }
    this.delete.emit(file);
  }

  protected onDownloadFile(fileId: string): void {
    const file = this.localFiles().find(f => f.id === fileId);
    this.fileService.download(file);
  }

  protected onPreviewFile(fileId: string): void {
    const data: CarouselDialogData = {
      files: this.viewableFiles,
      currentIndex: this.viewableFiles().findIndex(f => f.id === fileId),
      onDescriptionChanged: event => this.saveFileDescription(event)
    };
    this.filePreviewDialogService.open(data);
  }

  protected onSetAsAvatar(fileId: string): void {
    const file = this.localFiles().find(f => f.id === fileId);
    this.avatarSet.emit(file);
  }

  protected cancelUpload(uploadId: string | undefined): void {
    if (uploadId) {
      this.fileActions.cancelUpload(uploadId);
    }
  }

  protected dismissUpload(uploadId: string | undefined): void {
    if (uploadId) {
      this.fileActions.dismissUpload(uploadId);
    }
  }

  private saveFileDescription(event: { file: FileInfo; description: string }): void {
    // Not tied to this list: the carousel dialog that triggers the save can outlive it.
    this.fileActions
      .updateFile(event.file.id, { description: event.description, fileTags: [], orderNo: event.file.orderNo ?? 0 })
      .subscribe();
  }
}
